import {
  LANGUAGES,
  type Language,
  MAIN_GROUP_LISTS,
  type MainGroup,
  type MainGroupListId,
} from "@bt/parts-core";

// The main-group lists themselves (MAIN_GROUP_LISTS, MainGroup) are data in @bt/parts-core.

/** Tags for kinds of parts that turn up in many groups, offered after every list's groups. */
export const CROSS_GROUP_TAGS: readonly MainGroup[] = [
  { number: null, name: { en: "Clips", de: "Clips" } },
  { number: null, name: { en: "Washers", de: "Unterlegscheiben" } },
  { number: null, name: { en: "Seals", de: "Dichtungen" } },
  { number: null, name: { en: "Service Items", de: "Wartungsteile" } },
  { number: null, name: { en: "Tools", de: "Werkzeug" } },
];

/** What a list offers as tags: its groups, then the cross-group tags. */
export function mainGroupsOf(list: MainGroupListId): readonly MainGroup[] {
  return [...MAIN_GROUP_LISTS[list], ...CROSS_GROUP_TAGS];
}

/** "11 Engine": the number first, so tag lists sort in catalog order. */
export function mainGroupTagName(group: MainGroup, language: Language): string {
  const name = group.name[language];
  return group.number ? `${group.number} ${name}` : name;
}

/**
 * Tag names of a list's groups the workspace doesn't have yet, named in `language`. A group counts as
 * present when a tag carries its name in any language (ignoring case and surrounding spaces) or,
 * for a numbered group, starts with its number. So groups added before the language was switched,
 * or renamed later ("34 Brakes & ABS"), are kept as they are and not offered again.
 */
export function mainGroupTagsToAdd(
  existingNames: readonly string[],
  language: Language,
  list: MainGroupListId,
): string[] {
  const existing = existingNames.map((name) => name.trim().toLowerCase());
  return mainGroupsOf(list)
    .filter((group) => {
      const names = LANGUAGES.map((l) => mainGroupTagName(group, l).toLowerCase());
      return !existing.some(
        (tag) =>
          names.includes(tag) || (group.number !== null && tag.startsWith(`${group.number} `)),
      );
    })
    .map((group) => mainGroupTagName(group, language));
}

/**
 * The main group a tag stands for, as "<list>:<number or name>", when its name is one a list
 * offers in any language; null for a garage's own tag. Main groups are public reference data, so
 * the report lists them without the 3-garage rule (ADR 0016).
 */
export function mainGroupKeyOf(tagName: string): string | null {
  const name = tagName.trim().toLowerCase();
  for (const list of Object.keys(MAIN_GROUP_LISTS) as MainGroupListId[]) {
    for (const group of mainGroupsOf(list)) {
      if (LANGUAGES.some((l) => mainGroupTagName(group, l).toLowerCase() === name)) {
        return group.number ? `${list}:${group.number}` : `any:${group.name.en.toLowerCase()}`;
      }
    }
  }
  return null;
}
